/**
 * CourseCard: the course as a small, light picture for a card (the watch
 * screen's swap card), drawn without Mapbox: the whole course framed as a
 * plain line on the canvas tone (every course of an event with several, each
 * in its own colour), and each located camera as a small dot. It shows no
 * tiles, so nothing of Mapbox's (and no attribution) is in it.
 *
 * Geometry is in a 160×90 box (the card's 16:9), projected with Web
 * Mercator as the map is; the surface's stylesheet gives it colours (a lone
 * course's line ink on a white casing, each of several courses its own colour
 * on an ink one, dots green while live, grey otherwise, a pink ring on the
 * watched camera). Decorative: the card's own button names it.
 */

import { useMemo } from 'react'

/** The card's box, in the drawing's units: the line's widths and the dots' places are measured in it. */
export const CARD_BOX = { width: 160, height: 90 } as const
/** Room around what the card frames, in the box's units: clear of its edges, its label and its expand control. */
export const CARD_PADDING = { x: 16, y: 14 } as const
/**
 * How far (in the box's units, about half a pixel on the widest card) the drawn line may stray from the course: a
 * GPX's thousands of points become a simple line of a hundred or so, with no corners the course doesn't have.
 */
const SIMPLIFY_TOLERANCE = 0.3

type LngLat = readonly [longitude: number, latitude: number]

export interface CourseCardDot {
  identity: string
  coordinate: { latitude: number; longitude: number }
  /** Video is in LiveKit and the viewer's connection is up: the only green. */
  live: boolean
  /** The watched camera: a pink ring. */
  selected: boolean
}

/** One course's line on the card. */
export interface CourseCardCourse {
  /** Its points as [longitude, latitude], in order. Empty before its GPX arrives. */
  line: readonly LngLat[]
  /** Its own colour, for one of several courses; a lone course is left to the stylesheet's ink. */
  color?: string
}

/** Keep each array stable between renders (memoise them): a new one reprojects the card. */
export interface CourseCardProps {
  /** The event's courses, drawn in order (the last on top). Empty with no course. */
  courses: readonly CourseCardCourse[]
  /** What to frame when there is no line yet: the course's bounds' corners (from the payload), if any. */
  bounds: readonly LngLat[]
  dots: CourseCardDot[]
}

/** Web Mercator, as a fraction of the world. */
function mercator([longitude, latitude]: LngLat): [number, number] {
  const sin = Math.sin((latitude * Math.PI) / 180)
  return [(longitude + 180) / 360, 0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)]
}

/**
 * Places points of the world in the card's box so that `frame` fills the box's room (inside the padding), centred,
 * at one scale for both axes. A frame with no extent (one point) sits in the middle.
 */
export function cardProjection(frame: readonly LngLat[]): (point: LngLat) => [number, number] {
  const projected = frame.map(mercator)
  let [minX, minY, maxX, maxY] = [Infinity, Infinity, -Infinity, -Infinity]
  for (const [x, y] of projected) {
    minX = Math.min(minX, x)
    maxX = Math.max(maxX, x)
    minY = Math.min(minY, y)
    maxY = Math.max(maxY, y)
  }
  const room = { width: CARD_BOX.width - 2 * CARD_PADDING.x, height: CARD_BOX.height - 2 * CARD_PADDING.y }
  const scales = [maxX > minX ? room.width / (maxX - minX) : Infinity, maxY > minY ? room.height / (maxY - minY) : Infinity]
  const scale = Math.min(...scales)
  const fit = Number.isFinite(scale) ? scale : 0
  const centre = [(minX + maxX) / 2, (minY + maxY) / 2]
  return (point) => {
    const [x, y] = mercator(point)
    return [CARD_BOX.width / 2 + (x - centre[0]) * fit, CARD_BOX.height / 2 + (y - centre[1]) * fit]
  }
}

/** The squared distance from `point` to the segment from `start` to `end`. */
function segmentDistanceSquared([x, y]: [number, number], [x1, y1]: [number, number], [x2, y2]: [number, number]) {
  const dx = x2 - x1
  const dy = y2 - y1
  const length = dx * dx + dy * dy
  const t = length ? Math.max(0, Math.min(1, ((x - x1) * dx + (y - y1) * dy) / length)) : 0
  const [px, py] = [x1 + t * dx - x, y1 + t * dy - y]
  return px * px + py * py
}

/** Douglas–Peucker: the fewest points that keep the line within `tolerance` of the course. */
export function simplified(points: [number, number][], tolerance: number) {
  if (points.length < 3) return points
  const keep = new Uint8Array(points.length)
  keep[0] = keep[points.length - 1] = 1
  const spans: [number, number][] = [[0, points.length - 1]]
  while (spans.length) {
    const [first, last] = spans.pop()!
    let farthest = -1
    let distance = tolerance * tolerance
    for (let index = first + 1; index < last; index++) {
      const away = segmentDistanceSquared(points[index], points[first], points[last])
      if (away > distance) [farthest, distance] = [index, away]
    }
    if (farthest < 0) continue
    keep[farthest] = 1
    spans.push([first, farthest], [farthest, last])
  }
  return points.filter((_, index) => keep[index])
}

const percent = (value: number, of: number) => `${((value / of) * 100).toFixed(2)}%`

export function CourseCard({ courses, bounds, dots }: CourseCardProps) {
  // Every course's line frames the card; before the lines arrive, the bounds; without a course, the located cameras.
  const project = useMemo(() => {
    const drawn = courses.flatMap((course) => course.line)
    const frame = drawn.length ? drawn : bounds.length ? bounds : dots.map(({ coordinate }) => [coordinate.longitude, coordinate.latitude] as const)
    return frame.length ? cardProjection(frame) : null
  }, [courses, bounds, dots])
  const lines = useMemo(() => {
    if (!project) return []
    return courses
      .filter((course) => course.line.length >= 2)
      .map((course) => ({
        color: course.color,
        d: simplified(course.line.map(project), SIMPLIFY_TOLERANCE)
          .map(([x, y], index) => `${index ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`)
          .join(''),
      }))
  }, [courses, project])

  return (
    <div className="lv-course-card" aria-hidden="true">
      {lines.length > 0 && (
        <svg className="lv-course-card__course" viewBox={`0 0 ${CARD_BOX.width} ${CARD_BOX.height}`} preserveAspectRatio="none">
          {/* Widths in the box's units, so the line keeps its weight against the card at any card size. */}
          {lines.map(({ d, color }, index) => (
            <g key={index}>
              <path className="lv-course-card__casing" d={d} strokeWidth={4} data-coloured={color ? '' : undefined} />
              <path className="lv-course-card__line" d={d} strokeWidth={2} style={color ? { stroke: color } : undefined} />
            </g>
          ))}
        </svg>
      )}
      {project &&
        dots.map((dot) => {
          const [x, y] = project([dot.coordinate.longitude, dot.coordinate.latitude])
          return (
            <span
              key={dot.identity}
              className="lv-course-card__dot"
              data-identity={dot.identity}
              data-live={dot.live || undefined}
              data-selected={dot.selected || undefined}
              style={{ left: percent(x, CARD_BOX.width), top: percent(y, CARD_BOX.height) }}
            />
          )
        })}
    </div>
  )
}
